/**
 * LeaderPortalService
 * Leader self-service portal: my cases, my sessions, dashboard stats, notes and
 * availability updates
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for the leader portal (current authenticated leader)
 */
export class LeaderPortalService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LeaderPortalService.DEFAULT_SERVICE_PATH,
            basePath: LeaderPortalService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get cases assigned to the current leader (privacy-trimmed)
     * @returns Array of leader case views
     */
    async myCases() {
        const response = await this.myCasesRaw();
        return response.data ?? [];
    }
    /**
     * Get cases assigned to the current leader with full response
     * @returns ResponseData with array of leader case views
     */
    myCasesRaw() {
        const url = this.buildUrl('my-cases');
        return this.http.get(url);
    }
    /**
     * Get the current leader's cases filtered by a CareCaseStatus (e.g. pending = the "assigned"
     * status). Scoped to the logged-in leader's UserId server-side.
     */
    async myCasesByStatus(statusId) {
        const response = await this.myCasesByStatusRaw(statusId);
        return response.data ?? [];
    }
    myCasesByStatusRaw(statusId) {
        const url = this.buildUrl(`my-cases/by-status/${statusId}`);
        return this.http.get(url);
    }
    /**
     * Get cases assigned to the current leader that they have never opened (accepted only)
     * @returns Array of leader case views, oldest assignment first
     */
    async myUnviewedCases() {
        const response = await this.myUnviewedCasesRaw();
        return response.data ?? [];
    }
    /**
     * Get cases assigned to the current leader that they have never opened, with full response
     * @returns ResponseData with array of leader case views
     */
    myUnviewedCasesRaw() {
        const url = this.buildUrl('my-cases/unviewed');
        return this.http.get(url);
    }
    /**
     * Stream my cases in batches (fetch-once, chunk client-side — portal lists are small).
     * Yields one `LeaderCaseView[]` per batch so the UI can paint progressively.
     */
    async *streamMyCases(chunkSize = 50) {
        const all = await this.myCases();
        for (let i = 0; i < all.length; i += chunkSize) {
            yield all.slice(i, i + chunkSize);
        }
    }
    /** Stream my cases filtered by a CareCaseStatus, in batches. */
    async *streamMyCasesByStatus(statusId, chunkSize = 50) {
        const all = await this.myCasesByStatus(statusId);
        for (let i = 0; i < all.length; i += chunkSize) {
            yield all.slice(i, i + chunkSize);
        }
    }
    /**
     * Get the detail of a case assigned to the current leader
     * (admin-only notes are excluded server-side)
     * @param caseId - The case ID
     * @returns Care case model view
     */
    async caseDetail(caseId) {
        const response = await this.caseDetailRaw(caseId);
        if (!response.data) {
            throw new Error(`Care case with ID ${caseId} not found`);
        }
        return response.data;
    }
    /**
     * Get the detail of a case assigned to the current leader with full response
     * @param caseId - The case ID
     * @returns ResponseData with care case model view
     */
    caseDetailRaw(caseId) {
        const url = this.buildUrl(`cases/${caseId}`);
        return this.http.get(url);
    }
    /**
     * Get sessions the current leader runs
     * @returns Array of leader session views
     */
    async mySessions(caseId) {
        const response = await this.mySessionsRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get sessions the current leader runs with full response, optionally scoped to a single case
     * (exact server-side filter — no case-name substring matching).
     * @param caseId - Restrict to sessions linked to this case
     */
    mySessionsRaw(caseId) {
        const url = this.buildUrl(caseId != null ? `my-sessions?caseId=${caseId}` : 'my-sessions');
        return this.http.get(url);
    }
    /**
     * Get dashboard statistics for the current leader
     * @returns Leader dashboard statistics
     */
    async dashboardStats() {
        const response = await this.dashboardStatsRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve leader dashboard stats');
        }
        return response.data;
    }
    /**
     * Get dashboard statistics for the current leader with full response
     * @returns ResponseData with leader dashboard statistics
     */
    dashboardStatsRaw() {
        const url = this.buildUrl('dashboard-stats');
        return this.http.get(url);
    }
    /**
     * Add a note to one of the current leader's cases
     * @param caseId - The case ID
     * @param data - Note payload
     * @returns The created case note
     */
    async addCaseNote(caseId, data) {
        const response = await this.addCaseNoteRaw(caseId, data);
        if (!response.data) {
            throw new Error(`Failed to add note to care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Add a note to one of the current leader's cases with full response
     * @param caseId - The case ID
     * @param data - Note payload
     * @returns ResponseData with the created case note
     */
    addCaseNoteRaw(caseId, data) {
        const url = this.buildUrl(`cases/${caseId}/notes`);
        return this.http.post(url, data);
    }
    /**
     * Add a note to one of the current leader's sessions
     * @param sessionId - The session ID
     * @param data - Note payload
     * @returns The created session note
     */
    async addSessionNote(sessionId, data) {
        const response = await this.addSessionNoteRaw(sessionId, data);
        if (!response.data) {
            throw new Error(`Failed to add note to care session ${sessionId}`);
        }
        return response.data;
    }
    /**
     * Add a note to one of the current leader's sessions with full response
     * @param sessionId - The session ID
     * @param data - Note payload
     * @returns ResponseData with the created session note
     */
    addSessionNoteRaw(sessionId, data) {
        const url = this.buildUrl(`sessions/${sessionId}/notes`);
        return this.http.post(url, data);
    }
    /**
     * Replace the current leader's weekly availability slots
     * @param data - The replacement slots
     * @returns The saved availability slots
     */
    async updateAvailability(data) {
        const response = await this.updateAvailabilityRaw(data);
        return response.data ?? [];
    }
    /**
     * Replace the current leader's weekly availability slots with full response
     * @param data - The replacement slots
     * @returns ResponseData with the saved availability slots
     */
    updateAvailabilityRaw(data) {
        const url = this.buildUrl('availability');
        return this.http.put(url, data);
    }
    // ---------------------------------------------------------------------------
    // Case acceptance (status 1 = Assigned → accept 2 In Progress / decline 0 New)
    // ---------------------------------------------------------------------------
    /** Cases assigned to me that are still awaiting my accept/decline. */
    async myPendingCases() {
        const res = await this.http.get(this.buildUrl('my-pending-cases'));
        return res.data ?? [];
    }
    /** Accept an assigned case — moves it to In Progress. */
    async acceptCase(caseId) {
        const res = await this.http.patch(this.buildUrl(`cases/${caseId}/accept`));
        if (!res.data)
            throw new Error(`Failed to accept case ${caseId}`);
        return res.data;
    }
    /** Decline an assigned case — drops my assignment and returns it to the unassigned queue. */
    async declineCase(caseId) {
        const res = await this.http.patch(this.buildUrl(`cases/${caseId}/decline`));
        if (!res.data)
            throw new Error(`Failed to decline case ${caseId}`);
        return res.data;
    }
    /**
     * Change the status of a case I'm assigned to (e.g. In Progress → Resolved → Closed).
     * PATCH cases/{caseId}/change-status/{newStatus}
     * Assignment states are owned by accept/decline: setting Assigned (1) or New (0) is
     * rejected with 400 — use {@link acceptCase} / {@link declineCase} instead.
     */
    async changeCaseStatus(caseId, newStatus) {
        const res = await this.http.patch(this.buildUrl(`cases/${caseId}/change-status/${newStatus}`));
        if (!res.data)
            throw new Error(`Failed to change status for case ${caseId}`);
        return res.data;
    }
    // ---------------------------------------------------------------------------
    // Case notes (admin-only notes are filtered out server-side)
    // ---------------------------------------------------------------------------
    async caseNotes(caseId) {
        const res = await this.http.get(this.buildUrl(`cases/${caseId}/notes`));
        return res.data ?? [];
    }
    /** Update a case note you authored. */
    async updateCaseNote(caseId, noteId, data) {
        const res = await this.http.put(this.buildUrl(`cases/${caseId}/notes/${noteId}`), data);
        if (!res.data)
            throw new Error(`Failed to update note ${noteId} on case ${caseId}`);
        return res.data;
    }
    /** Delete a case note you authored. */
    async deleteCaseNote(caseId, noteId) {
        const res = await this.http.delete(this.buildUrl(`cases/${caseId}/notes/${noteId}`));
        return res.success;
    }
    // ---------------------------------------------------------------------------
    // Counsellees + case files
    // ---------------------------------------------------------------------------
    /** The counsellees (members) on a case I'm assigned to. */
    async counsellees(caseId) {
        const res = await this.http.get(this.buildUrl(`cases/${caseId}/counsellees`));
        return res.data ?? [];
    }
    async caseFiles(caseId) {
        const res = await this.http.get(this.buildUrl(`cases/${caseId}/files`));
        return res.data ?? [];
    }
    async addCaseFile(caseId, data) {
        const res = await this.http.post(this.buildUrl(`cases/${caseId}/files`), data);
        if (!res.data)
            throw new Error(`Failed to add file to case ${caseId}`);
        return res.data;
    }
    async deleteCaseFile(caseId, fileId) {
        const res = await this.http.delete(this.buildUrl(`cases/${caseId}/files/${fileId}`));
        return res.success;
    }
    // ---------------------------------------------------------------------------
    // Sessions I run
    // ---------------------------------------------------------------------------
    /** Schedule a session on a case I'm assigned to (I run it). */
    async addCaseSession(caseId, data) {
        const res = await this.http.post(this.buildUrl(`cases/${caseId}/sessions`), data);
        if (!res.data)
            throw new Error(`Failed to schedule a session on case ${caseId}`);
        return res.data;
    }
    /** Update one of my own sessions. */
    async updateSession(sessionId, data) {
        const res = await this.http.put(this.buildUrl(`sessions/${sessionId}`), data);
        if (!res.data)
            throw new Error(`Failed to update session ${sessionId}`);
        return res.data;
    }
    // ---------------------------------------------------------------------------
    // Session notes (admin-only notes are filtered out server-side)
    // ---------------------------------------------------------------------------
    async sessionNotes(sessionId) {
        const res = await this.http.get(this.buildUrl(`sessions/${sessionId}/notes`));
        return res.data ?? [];
    }
    /** Update a session note you authored. */
    async updateSessionNote(sessionId, noteId, data) {
        const res = await this.http.put(this.buildUrl(`sessions/${sessionId}/notes/${noteId}`), data);
        if (!res.data)
            throw new Error(`Failed to update note ${noteId} on session ${sessionId}`);
        return res.data;
    }
    /** Delete a session note you authored. */
    async deleteSessionNote(sessionId, noteId) {
        const res = await this.http.delete(this.buildUrl(`sessions/${sessionId}/notes/${noteId}`));
        return res.success;
    }
}
LeaderPortalService.DEFAULT_SERVICE_PATH = 'ac-counselling';
LeaderPortalService.BASE_PATH = 'api/portal/leader';
//# sourceMappingURL=LeaderPortalService.js.map